import React, { useEffect, useRef, useState } from 'react';

import { verificationCheckResult } from '@personal-trainer-app/shared';

import { EmailVerificationPrompt } from '../EmailVerificationPrompt';

import type { VerificationCheck } from '@personal-trainer-app/shared';

import { useDocumentTitle } from '@/hooks';
import { useAuth, useSendVerificationLink } from '@/hooks/auth';
import { useAuthStore } from '@/stores';

/**
 * EmailVerificationGate — what a signed-in account whose email isn't
 * verified sees in place of the whole app (email verification & two-factor
 * PRD, Task 2.2). The API refuses such an account almost every call, so
 * nothing behind the gate loads. It names the address, and offers a new
 * link, a check for a link opened elsewhere, and signing out.
 *
 * It shows at the address asked for, so once the account is verified the
 * guard lifts it there: after Check again, a new link answered "verified
 * already", or coming back to the tab, where the session is read again.
 */
export function EmailVerificationGate(): React.JSX.Element {
  const email = useAuthStore((state) => state.user?.email);
  const { logout } = useAuth();
  const link = useSendVerificationLink();
  const [check, setCheck] = useState<VerificationCheck | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // A verified account lifts the gate, which unmounts before a check ends.
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useDocumentTitle('Verify your email — Dō');

  // A new link changes the card under the button that asked for it, so focus
  // moves to its heading. Not on the first paint: nothing is sent yet.
  useEffect(() => {
    if (link.sentAt) headingRef.current?.focus();
  }, [link.sentAt]);

  const send = (): void => {
    setCheck(null);
    // Verified already: the user is read again, and the guard lifts the gate.
    void link.send();
  };

  const checkAgain = async (): Promise<void> => {
    if (check === 'checking') return;
    setCheck('checking');
    link.clearFailure();
    const read = await useAuthStore.getState().fetchUser();
    // Verified: the guard lifts the gate, and this is gone.
    if (!mounted.current) return;
    setCheck(
      verificationCheckResult(
        read,
        useAuthStore.getState().user?.email_verified
      )
    );
  };

  return (
    <EmailVerificationPrompt
      email={email}
      sentAt={link.sentAt}
      remaining={link.remaining}
      sending={link.sending}
      failure={link.failure}
      check={check}
      onSend={send}
      onCheckAgain={() => void checkAgain()}
      onSignOut={() => void logout()}
      headingRef={headingRef}
    />
  );
}

export default EmailVerificationGate;
